import { Table, TableHeader, TableBody, Pagination } from '../../../components/table';
import ActionIconButton from '../../../components/ui/ActionIconButton';
import Search from '../../../components/table/Search';
import React, { useCallback, useEffect, useState } from 'react';
import NoData from '../../../components/common/NoData';
import { Dialog, Button, Form } from '../../../components/ui';
import { toast } from '../../../components/ui/notify';
import useDebounce from '../../../hooks/useDebounce';
import {
  listDeletedAccounts,
  restoreDeletedAccount,
  permanentDeleteAccount,
} from '../../../services/organization/deletedAccounts/deletedAccounts.service';

import { useTimezone } from '../../../context/TimezoneContext';
import { formatUtcInTimeZone } from '../../../utils/timezone';
import { usePermissions } from '../../../context/PermissionsContext';
import StatusBadge from '../../../components/ui/StatusBadge';

const STATUS_LABELS = {
  PENDING_PERMANENT: 'Pending deletion',
  RESTORED: 'Restored',
  PERMANENTLY_DELETED: 'Permanently deleted',
};

function getStatusBadgeTone(status) {
  return {
    PENDING_PERMANENT: 'warning',
    RESTORED: 'success',
    PERMANENTLY_DELETED: 'danger',
  }[status] || 'neutral';
}

function DeletedAccounts() {
  const { timeZone } = useTimezone();
  const { permissions } = usePermissions();
  const canRecover = Boolean(permissions['settings_account_deletion-account_recover']);
  const canPermanentDelete = Boolean(permissions['settings_account_deletion-delete']);
  const formatDateShort = (value) => formatUtcInTimeZone(value, timeZone, 'DD MMM YYYY');
  const formatDateFull = (value) => formatUtcInTimeZone(value, timeZone, 'DD MMM YYYY, hh:mm A');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [deletionStatus, setDeletionStatus] = useState('PENDING_PERMANENT');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({ totalPages: 1, totalCount: 0 });
  const [confirmAction, setConfirmAction] = useState(null);
  const [viewRow, setViewRow] = useState(null);
  const debouncedSearch = useDebounce(search, 400);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listDeletedAccounts({
        page,
        limit: pageSize,
        search: debouncedSearch,
        deletion_status: deletionStatus || undefined,
      });
      if (response?.success) {
        setRows(response.data?.rows || []);
        setPagination(response.data?.pagination || { totalPages: 1, totalCount: 0 });
      } else {
        toast.error(response?.message || 'Failed to load deleted accounts');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load deleted accounts');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, deletionStatus]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleRestore = async (deletedAccountId) => {
    try {
      const response = await restoreDeletedAccount({ deleted_account_id: deletedAccountId });
      if (response?.success) {
        toast.success('Account restored successfully');
        setViewRow(null);
        fetchList();
      } else {
        toast.error(response?.message || 'Restore failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Restore failed');
    } finally {
      setConfirmAction(null);
    }
  };

  const handlePermanentDelete = async (deletedAccountId) => {
    try {
      const response = await permanentDeleteAccount({
        deleted_account_id: deletedAccountId,
        confirm: true,
      });
      if (response?.success) {
        toast.success('Account permanently deleted');
        setViewRow(null);
        fetchList();
      } else {
        toast.error(response?.message || 'Permanent delete failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Permanent delete failed');
    } finally {
      setConfirmAction(null);
    }
  };

  const renderStatusBadge = (status) => (
    <StatusBadge tone={getStatusBadgeTone(status)}>
      {STATUS_LABELS[status] || status}
    </StatusBadge>
  );

  const renderDetailRow = (label, value) => (
    <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-py-2 tw-border-b"}>
      <div className="!tw-text-[var(--secondary-color)] tw-min-w-[0px] ui-column tw-w-5/12 tw-text-slate-500">{label}</div>
      <div className="![font-weight:var(--font-weight-medium)] tw-min-w-[0px] ui-column tw-w-7/12 tw-font-medium">{value ?? '—'}</div>
    </div>
  );

  return (
    <div>
      <div className="tw-mb-6">
        <h3 className="tw-mb-1">Deleted Accounts</h3>
        <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mb-0">View, restore, or permanently delete user accounts</p>
      </div>

      <div className={"tw-flex tw-flex-wrap tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] [--grid-x:1rem] [--grid-y:1rem] ui-row tw-gap-grid-3 tw-mb-6"}>
        <div className="max-[768px]:!tw-w-[50%] tw-min-w-[0px] ui-column md:tw-w-1/2">
          <Search
            type="search"
            placeholder="Search by name or email"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
          />
        </div>
        <div className="tw-min-w-[0px] ui-column md:tw-w-1/3">
          <Form.Select
            value={deletionStatus}
            onChange={(e) => {
              setPage(1);
              setDeletionStatus(e.target.value);
            }}
          >
            <option value="">All statuses</option>
            <option value="PENDING_PERMANENT">Pending deletion</option>
            <option value="RESTORED">Restored</option>
          </Form.Select>
        </div>
      </div>

      <div className="tw-w-full tw-overflow-x-auto tw-rounded-[0.75rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [-webkit-overflow-scrolling:touch] ui-table-scroll">
        <Table className={"[&_.deleted-accounts-actions_.ui-button-sm]:!tw-w-auto [&_.deleted-accounts-actions_.ui-button-sm]:!tw-h-auto [&_.deleted-accounts-actions_.ui-button-sm]:!tw-min-h-[32px] [&_.deleted-accounts-actions_.ui-button-sm]:!tw-p-[0.45rem_1.25rem] [&_.deleted-accounts-actions_.ui-button-sm]:!tw-inline-flex [&_.deleted-accounts-actions_.ui-button-sm]:!tw-items-center [&_.deleted-accounts-actions_.ui-button-sm]:!tw-justify-center [&_.deleted-accounts-actions_.btn-view]:!tw-min-w-[36px] [&_.deleted-accounts-actions_.btn-view]:!tw-p-[0.45rem_0.7rem] [&_.deleted-accounts-actions_.btn-restore]:!tw-min-w-[92px] [&_.deleted-accounts-actions_.btn-restore]:!tw-pl-[1.35rem] [&_.deleted-accounts-actions_.btn-restore]:!tw-pr-[1.35rem] [&_.deleted-accounts-actions_.btn-delete]:!tw-min-w-[92px] [&_.deleted-accounts-actions_.btn-delete]:!tw-pl-[1.35rem] [&_.deleted-accounts-actions_.btn-delete]:!tw-pr-[1.35rem] tw-w-full tw-border-collapse tw-text-left tw-text-[0.875rem] tw-leading-[1.25rem] [&_>_thead]:[--tw-bg-opacity:1] [&_>_thead]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&_th]:tw-whitespace-nowrap [&_th]:tw-border-b-[1px] [&_th]:[--tw-border-opacity:1] [&_th]:tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [&_th]:tw-pl-4 [&_th]:tw-pr-4 [&_th]:tw-pt-3 [&_th]:tw-pb-3 [&_th]:tw-text-[0.75rem] [&_th]:tw-leading-[1rem] [&_th]:tw-font-semibold [&_th]:[--tw-text-opacity:1] [&_th]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] [&_td]:tw-border-b-[1px] [&_td]:[--tw-border-opacity:1] [&_td]:tw-border-[rgb(241_245_249_/_var(--tw-border-opacity,_1))] [&_td]:tw-pl-4 [&_td]:tw-pr-4 [&_td]:tw-pt-[0.875rem] [&_td]:tw-pb-[0.875rem] [&_td]:tw-align-middle [&_td]:[--tw-text-opacity:1] [&_td]:tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&_tbody_tr:last-child_td]:tw-border-b-[0px] [&_tbody_tr]:[transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [&_tbody_tr]:[transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [&_tbody_tr]:[transition-duration:150ms] [&_tbody_tr:hover]:[--tw-bg-opacity:1] [&_tbody_tr:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-table ui-table-hover tw-align-middle tw-mb-0"}>
          <TableHeader className="[--tw-bg-opacity:1] tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-table-light">
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Recover until</th>
              <th>Status</th>
              <th className="tw-text-right" style={{ minWidth: 220 }}>Actions</th>
            </tr>
          </TableHeader>
          <TableBody>
            {loading ? (
              <tr>
                <td colSpan="5" className="tw-text-center tw-py-6">Loading...</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan="5" className="tw-p-0">
                  <NoData message="No deleted accounts found" />
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id}>
                  <td className="![font-weight:var(--font-weight-medium)] tw-font-medium">{row.name || '—'}</td>
                  <td className="tw-truncate" style={{ maxWidth: 220 }} title={row.email}>
                    {row.email}
                  </td>
                  <td>{formatDateShort(row.scheduled_permanent_deletion_date)}</td>
                  <td>{renderStatusBadge(row.deletion_status)}</td>
                  <td className="tw-text-right">
                    <div className="tw-gap-3 tw-inline-flex tw-items-center tw-flex-nowrap deleted-accounts-actions">
                      <ActionIconButton
                        icon="tabler:eye"
                        label="View details"
                        tone="neutral"
                        type="button"
                        title="View details"
                        onClick={() => setViewRow(row)}
                      />
                      {row.deletion_status === 'PENDING_PERMANENT' && (
                        <>
                          {canRecover && (
                            <button
                              type="button"
                              className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] tw-rounded-[0.375rem] tw-pl-3 tw-pr-3 tw-pt-1.5 tw-pb-1.5 tw-text-[0.75rem] tw-leading-[1rem] ui-button ui-button-sm ui-button-primary tw-whitespace-nowrap btn-restore"}
                              disabled={Number(row.days_remaining) <= 0}
                              onClick={() => setConfirmAction({ type: 'restore', row })}
                            >
                              Restore
                            </button>
                          )}
                          {canPermanentDelete && (
                            <button
                              type="button"
                              className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-font-medium [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-border-[transparent] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-bg-opacity:1] tw-bg-[rgb(225_29_72_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(190_18_60_/_var(--tw-bg-opacity,_1))] tw-rounded-[0.375rem] tw-pl-3 tw-pr-3 tw-pt-1.5 tw-pb-1.5 tw-text-[0.75rem] tw-leading-[1rem] ui-button ui-button-sm ui-button-danger tw-whitespace-nowrap btn-delete"}
                              onClick={() => setConfirmAction({ type: 'permanent', row })}
                            >
                              Delete
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        page={page}
        pageSize={pageSize}
        buttonVariant="outline-secondary"
        buttonSize="sm"
        totalRecords={pagination.totalCount}
        totalPages={pagination.totalPages}
        disabled={loading}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
      />
      {/* View details modal */}
      <Dialog show={!!viewRow} onHide={() => setViewRow(null)} centered size="lg">
        <Dialog.Header closeButton>
          <Dialog.Title>Account deletion details</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body>
          {viewRow && (
            <div className="tw-px-1">
              {renderDetailRow('Name', viewRow.name)}
              {renderDetailRow('Email', viewRow.email)}
              {renderDetailRow('Deleted on', formatDateFull(viewRow.deletion_date))}
              {renderDetailRow('Recover until', formatDateFull(viewRow.scheduled_permanent_deletion_date))}
              {renderDetailRow('Days remaining', viewRow.days_remaining ?? '—')}
              {renderDetailRow('Deleted by', viewRow.deleted_by)}
              {renderDetailRow('Deletion reason', viewRow.deletion_reason || 'Not provided')}
              {renderDetailRow('Deletion status', renderStatusBadge(viewRow.deletion_status))}
              {renderDetailRow('Recovery status', viewRow.recovery_status?.replace(/_/g, ' '))}
              {viewRow.restored_at && renderDetailRow('Restored on', formatDateFull(viewRow.restored_at))}
            </div>
          )}
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="outline-secondary" onClick={() => setViewRow(null)}>Close</Button>
          {viewRow?.deletion_status === 'PENDING_PERMANENT' && (
            <>
              {canRecover && (
                <Button
                  variant="primary"
                  disabled={Number(viewRow.days_remaining) <= 0}
                  onClick={() => {
                    setViewRow(null);
                    setConfirmAction({ type: 'restore', row: viewRow });
                  }}
                >
                  Restore account
                </Button>
              )}
              {canPermanentDelete && (
                <Button
                  variant="danger"
                  onClick={() => {
                    setViewRow(null);
                    setConfirmAction({ type: 'permanent', row: viewRow });
                  }}
                >
                  Delete permanently
                </Button>
              )}
            </>
          )}
        </Dialog.Footer>
      </Dialog>

      {/* Confirm action modal */}
      {confirmAction && (
        <Dialog show onHide={() => setConfirmAction(null)} centered>
          <Dialog.Header closeButton>
            <Dialog.Title>
              {confirmAction.type === 'restore' ? 'Restore account' : 'Permanently delete account'}
            </Dialog.Title>
          </Dialog.Header>
          <Dialog.Body>
            {confirmAction.type === 'restore' ? (
              <p className="tw-mb-0">
                Restore <strong>{confirmAction.row.email}</strong>? They will be able to log in again.
              </p>
            ) : (
              <p className="tw-mb-0">
                Permanently delete <strong>{confirmAction.row.email}</strong>? All data will be removed and this email can register again.
              </p>
            )}
          </Dialog.Body>
          <Dialog.Footer>
            <Button variant="outline-secondary" onClick={() => setConfirmAction(null)}>Cancel</Button>
            <Button
              variant={confirmAction.type === 'restore' ? 'primary' : 'danger'}
              onClick={() => {
                if (confirmAction.type === 'restore') {
                  handleRestore(confirmAction.row.id);
                } else {
                  handlePermanentDelete(confirmAction.row.id);
                }
              }}
            >
              Confirm
            </Button>
          </Dialog.Footer>
        </Dialog>
      )}
    </div>
  );
}

export default DeletedAccounts;
